// Gespeicherte Berichte bedienen (Agent 7A, R-07): Titelmenü mit den Berichten, „Speichern“ (Besitzer), „Speichern unter …“, „…“-Menü
// (CSV, Link, Umbenennen, Teilen, Kopie, Löschen). Jede Berichtsseite benutzt `useBerichtSteuerung` und `BerichtKopf`.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { berichteApi, type BerichtArt, type GespeicherterBericht } from "../api/berichte";
import { fehlerText } from "../api/client";
import { Dialog } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { kopieren } from "../werkzeuge/tasten";
import { filterAusKonfig, konfigAusFilter, normiert, type Konfig } from "./konfig";
import { BT } from "./texte";
import type { useBerichtFilter } from "./zustand";

type Filter = ReturnType<typeof useBerichtFilter>;

interface Optionen {
  art: BerichtArt;
  filter: Filter;
  /** seitenspezifische Teile der Konfiguration (Kennzahlen, Pipeline, Richtung …) */
  zusatz: Konfig;
  /** gespeicherte Konfiguration auf die Seite anwenden (die Filter setzt die Steuerung selbst) */
  onAnwenden: (k: Konfig) => void;
}

const FILTER_PARAMETER = ["z", "v", "u", "sv"];

export function useBerichtSteuerung({ art, filter, zusatz, onAnwenden }: Optionen) {
  const meldung = useMeldung();
  const [alle, setAlle] = useState<GespeicherterBericht[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const angewendet = useRef<string | null>(null);
  const anwenden = useRef(onAnwenden);
  anwenden.current = onAnwenden;
  const { werte, setze, suche } = filter;

  const laden = useCallback(async () => {
    try {
      const r = await berichteApi.gespeicherte();
      setAlle(r.data);
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, []);
  useEffect(() => {
    void laden();
  }, [laden]);

  const liste = useMemo(() => (alle ?? []).filter((b) => b.art === art), [alle, art]);
  const standard = useMemo(() => liste.find((b) => b.ist_standard && !b.besitzer_id) ?? liste.find((b) => b.ist_standard) ?? liste[0] ?? null, [liste]);
  const aktiv = useMemo(() => (werte.berichtId ? liste.find((b) => b.id === werte.berichtId) ?? null : null), [liste, werte.berichtId]);
  const bericht = aktiv ?? standard;

  // Beim ersten Mal (und wenn ein anderer Bericht gewählt wurde) die Konfiguration anwenden. Filter in der Adresse gehen vor.
  useEffect(() => {
    if (!bericht || angewendet.current === bericht.id) return;
    angewendet.current = bericht.id;
    const sp = new URLSearchParams(suche);
    if (!FILTER_PARAMETER.some((p) => sp.has(p))) setze(filterAusKonfig(bericht.konfiguration), true);
    anwenden.current(bericht.konfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bericht?.id]);

  const aktuell = useMemo(() => konfigAusFilter(werte, zusatz), [werte, zusatz]);
  const geaendert = !!bericht && angewendet.current === bericht.id && normiert(aktuell) !== normiert(bericht.konfiguration);

  const waehlen = useCallback(
    (b: GespeicherterBericht | null) => {
      const ziel = b ?? standard;
      if (!ziel) return;
      angewendet.current = ziel.id;
      setze({ ...filterAusKonfig(ziel.konfiguration), berichtId: b && !(b.ist_standard && !b.besitzer_id) ? b.id : null });
      anwenden.current(ziel.konfiguration);
    },
    [setze, standard],
  );

  const ersetzen = (neu: GespeicherterBericht) => setAlle((a) => (a ? a.map((x) => (x.id === neu.id ? neu : x)) : a));

  const speichern = useCallback(async () => {
    if (!bericht) return;
    try {
      const r = await berichteApi.aendern(bericht.id, { konfiguration: aktuell });
      ersetzen(r.bericht);
      meldung.ok("Bericht gespeichert.");
    } catch (e) {
      meldung.zeige("fehler", fehlerText(e));
    }
  }, [bericht, aktuell, meldung]);

  const speichernUnter = useCallback(
    async (name: string, geteilt: boolean) => {
      try {
        const r = await berichteApi.speichern({ name, art, konfiguration: aktuell, geteilt });
        setAlle((a) => [...(a ?? []), r.bericht]);
        angewendet.current = r.bericht.id;
        setze({ berichtId: r.bericht.id }, true);
        meldung.ok(`Bericht „${r.bericht.name}“ gespeichert.`);
      } catch (e) {
        meldung.zeige("fehler", fehlerText(e));
      }
    },
    [art, aktuell, meldung, setze],
  );

  const aendern = useCallback(
    async (patch: { name?: string; geteilt?: boolean }) => {
      if (!bericht) return;
      try {
        const r = await berichteApi.aendern(bericht.id, patch);
        ersetzen(r.bericht);
      } catch (e) {
        meldung.zeige("fehler", fehlerText(e));
      }
    },
    [bericht, meldung],
  );

  const kopie = useCallback(async () => {
    if (!bericht) return;
    try {
      const r = await berichteApi.kopieren(bericht.id);
      setAlle((a) => [...(a ?? []), r.bericht]);
      waehlen(r.bericht);
      meldung.ok(`Kopie „${r.bericht.name}“ angelegt.`);
    } catch (e) {
      meldung.zeige("fehler", fehlerText(e));
    }
  }, [bericht, meldung, waehlen]);

  const loeschen = useCallback(async () => {
    if (!bericht) return;
    try {
      await berichteApi.loeschen(bericht.id);
      setAlle((a) => (a ? a.filter((x) => x.id !== bericht.id) : a));
      angewendet.current = null;
      setze({ berichtId: null }, true);
      meldung.ok("Bericht gelöscht.");
    } catch (e) {
      meldung.zeige("fehler", fehlerText(e));
    }
  }, [bericht, meldung, setze]);

  const zuruecksetzen = useCallback(() => {
    if (bericht) waehlen(aktiv);
  }, [bericht, aktiv, waehlen]);

  return { art, liste, bericht, aktiv, geaendert, fehler, waehlen, speichern, speichernUnter, aendern, kopie, loeschen, zuruecksetzen, geladen: alle !== null };
}

export type Steuerung = ReturnType<typeof useBerichtSteuerung>;

// ---------------------------------------------------------------------------------------------------------------------------

type DialogArt = null | "speichern_unter" | "umbenennen" | "loeschen";

interface KopfProps {
  steuerung: Steuerung;
  /** Menüpunkte oben im „…“-Menü (z. B. CSV) */
  zusatzPunkte?: MenuePunkt[];
  /** weitere Knöpfe rechts neben „Speichern unter“ */
  rechts?: ReactNode;
}

export function BerichtKopf({ steuerung: s, zusatzPunkte = [], rechts }: KopfProps) {
  const meldung = useMeldung();
  const [dialog, setDialog] = useState<DialogArt>(null);
  const b = s.bericht;
  const darf = !!b?.darf_aendern;
  const eigener = !!b && !b.ist_standard;

  const titelPunkte: MenuePunkt[] = [
    ...s.liste.map((x) => ({
      label: x.name,
      icon: x.id === b?.id ? ("haken" as const) : undefined,
      rechts: x.geteilt && !x.ist_standard ? <span className="leise">geteilt</span> : null,
      onClick: () => s.waehlen(x),
    })),
    { label: BT.neuerBericht + " aus dieser Ansicht …", icon: "plus", trenner: true, onClick: () => setDialog("speichern_unter") },
  ];
  const menuePunkte: MenuePunkt[] = [
    ...zusatzPunkte,
    {
      label: BT.linkKopieren,
      icon: "link",
      trenner: zusatzPunkte.length > 0,
      onClick: async () => {
        if (await kopieren(window.location.href)) meldung.ok("Link kopiert.");
        else meldung.zeige("fehler", "Kopieren hat nicht geklappt.");
      },
    },
    { label: "Kopie anlegen", icon: "kopieren", onClick: () => void s.kopie(), deaktiviert: !b },
    ...(darf
      ? ([
          { label: "Umbenennen …", icon: "stift", trenner: true, onClick: () => setDialog("umbenennen") },
          { label: b?.geteilt ? "Nicht mehr mit allen teilen" : "Mit allen teilen", icon: "person", onClick: () => void s.aendern({ geteilt: !b?.geteilt }), deaktiviert: !!b?.ist_standard && !!b?.geteilt },
        ] as MenuePunkt[])
      : []),
    ...(s.geaendert ? ([{ label: "Änderungen verwerfen", icon: "rueckgaengig", trenner: !darf, onClick: s.zuruecksetzen }] as MenuePunkt[]) : []),
    ...(darf && eigener ? ([{ label: "Bericht löschen …", icon: "papierkorb", gefahr: true, trenner: true, onClick: () => setDialog("loeschen") }] as MenuePunkt[]) : []),
  ];

  return (
    <>
      <div className="liste-kopf bericht-kopf">
        <MenueKnopf punkte={titelPunkte} knopfKlasse="bericht-titel" titel="Gespeicherte Berichte" breite={300}>
          <h1 className="liste-kopf__titel">{b?.name ?? BT.reiter[s.art]}</h1>
          <Icon name="chevron-unten" groesse={14} />
        </MenueKnopf>
        {b && !b.ist_standard && b.besitzer_name && !darf ? <span className="liste-kopf__info">von {b.besitzer_name}</span> : null}
        {s.geaendert ? <span className="bericht-geaendert">{BT.geaendert}</span> : null}
        <div className="liste-kopf__rechts">
          {rechts}
          {s.geaendert && darf ? (
            <button type="button" className="knopf knopf--primaer" onClick={() => void s.speichern()}>
              {BT.speichern}
            </button>
          ) : null}
          <button type="button" className={`knopf${s.geaendert && !darf ? " knopf--primaer" : ""}`} onClick={() => setDialog("speichern_unter")}>
            {BT.speichernUnter}
          </button>
          <MenueKnopf punkte={menuePunkte} knopfKlasse="symbolknopf symbolknopf--rand" titel="Mehr" ausrichtung="end" breite={240}>
            <Icon name="mehr" groesse={16} />
          </MenueKnopf>
        </div>
      </div>
      <NameDialog
        offen={dialog === "speichern_unter" || dialog === "umbenennen"}
        art={dialog === "umbenennen" ? "umbenennen" : "neu"}
        startName={dialog === "umbenennen" ? b?.name ?? "" : ""}
        onSchliessen={() => setDialog(null)}
        onOk={(name, geteilt) => {
          setDialog(null);
          if (dialog === "umbenennen") void s.aendern({ name });
          else void s.speichernUnter(name, geteilt);
        }}
      />
      <Dialog
        titel="Bericht löschen"
        offen={dialog === "loeschen"}
        onSchliessen={() => setDialog(null)}
        breite={420}
        fuss={
          <>
            <button type="button" className="knopf" onClick={() => setDialog(null)}>
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--gefahr"
              onClick={() => {
                setDialog(null);
                void s.loeschen();
              }}
            >
              Löschen
            </button>
          </>
        }
      >
        <p>„{b?.name}“ wird für alle gelöscht, auch wenn er geteilt war.</p>
      </Dialog>
    </>
  );
}

function NameDialog({
  offen, art, startName, onSchliessen, onOk,
}: {
  offen: boolean;
  art: "neu" | "umbenennen";
  startName: string;
  onSchliessen: () => void;
  onOk: (name: string, geteilt: boolean) => void;
}) {
  const [name, setName] = useState(startName);
  const [geteilt, setGeteilt] = useState(false);
  useEffect(() => {
    if (offen) {
      setName(startName);
      setGeteilt(false);
    }
  }, [offen, startName]);
  const ok = name.trim().length > 0 && name.trim().length <= 200;
  return (
    <Dialog
      titel={art === "neu" ? "Bericht speichern unter" : "Bericht umbenennen"}
      offen={offen}
      onSchliessen={onSchliessen}
      breite={420}
      fuss={
        <>
          <button type="button" className="knopf" onClick={onSchliessen}>
            Abbrechen
          </button>
          <button type="button" className="knopf knopf--primaer" disabled={!ok} onClick={() => onOk(name.trim(), geteilt)}>
            Speichern
          </button>
        </>
      }
    >
      <form
        className="bericht-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) onOk(name.trim(), geteilt);
        }}
      >
        <label className="feld">
          <span className="feld__label">Name</span>
          <input className="eingabe" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} placeholder="z. B. Wochenzahlen Setter" />
        </label>
        {art === "neu" ? (
          <label className="bericht-dialog__haken">
            <input type="checkbox" checked={geteilt} onChange={(e) => setGeteilt(e.target.checked)} />
            <span>Mit allen Nutzern teilen</span>
          </label>
        ) : null}
        <p className="leise">Gespeichert werden Zeitraum (als Name, z. B. „Diese Woche“), Vergleich, Nutzer, Leads und Kennzahlen.</p>
      </form>
    </Dialog>
  );
}
